import { useCallback, useEffect, useRef, useState } from "react";
import { useGT } from "gt-react";
import { Plus, X } from "lucide-react";
import type { RestParam } from "@spuerhund/shared/queryMetadata";

export function QueryParamsGrid({
  params,
  onChange,
}: {
  params: RestParam[];
  onChange: (params: RestParam[]) => void;
}) {
  const t = useGT();
  const gridOriginRef = useRef(false);
  const [rows, setRows] = useState<RestParam[]>(() => params.map((p) => ({ ...p })));

  // Sync from external changes (remote sync, query switch, URL-bar edits that
  // normalized into restParams). Skip the echo from our own onChange calls.
  // Use a stable string key so we re-run only on actual content changes.
  const serializedParams = JSON.stringify(params);
  useEffect(() => {
    if (gridOriginRef.current) {
      gridOriginRef.current = false;
      return;
    }
    setRows(JSON.parse(serializedParams) as RestParam[]);
  }, [serializedParams]);

  const commitRows = useCallback(
    (newRows: RestParam[]) => {
      setRows(newRows);
      gridOriginRef.current = true;
      onChange(newRows);
    },
    [onChange],
  );

  return (
    <div className="flex flex-1 flex-col overflow-y-auto p-3">
      {rows.length > 0 && (
        <div className="mb-1 flex items-center gap-1.5 px-1 text-[10px] font-medium uppercase tracking-wider text-zinc-600">
          <span className="flex-1">{t("Key")}</span>
          <span className="flex-1">{t("Value")}</span>
          <span className="w-6" />
        </div>
      )}
      <div className="space-y-1">
        {rows.map((row, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <input
              type="text"
              value={row.key}
              placeholder={t("key")}
              onChange={(e) => {
                const newRows = rows.map((r, j) =>
                  j === i ? { ...r, key: e.target.value } : r,
                );
                commitRows(newRows);
              }}
              className="flex-1 rounded border border-zinc-700 bg-surface px-2 py-1 font-mono text-xs text-zinc-200 placeholder:text-zinc-700 focus:border-accent focus:outline-none"
            />
            <input
              type="text"
              value={row.value}
              placeholder={t("value")}
              onChange={(e) => {
                const newRows = rows.map((r, j) =>
                  j === i ? { ...r, value: e.target.value } : r,
                );
                commitRows(newRows);
              }}
              className="flex-1 rounded border border-zinc-700 bg-surface px-2 py-1 font-mono text-xs text-zinc-200 placeholder:text-zinc-700 focus:border-accent focus:outline-none"
            />
            <button
              onClick={() => commitRows(rows.filter((_, j) => j !== i))}
              className="shrink-0 rounded p-1 text-zinc-600 hover:bg-surface-raised hover:text-zinc-400"
              title={t("Remove parameter")}
            >
              <X size={12} />
            </button>
          </div>
        ))}
      </div>
      <button
        onClick={() => commitRows([...rows, { key: "", value: "" }])}
        className="mt-2 flex items-center gap-1 rounded px-2 py-1 text-xs text-zinc-500 hover:bg-surface-raised hover:text-zinc-300"
      >
        <Plus size={12} />
        {t("Add parameter")}
      </button>
    </div>
  );
}
